import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { authMiddleware } from "~/lib/auth-middleware";
import { dbMiddleware } from "~/lib/db-middleware";
import {
  getActiveSummit,
  getSummitWithDays,
  getSessionWithSummit,
  getUserSummitRegistration,
  getUserSessionRegistrations,
  registerForSession as registerForSessionDb,
  unregisterFromSession as unregisterFromSessionDb,
  transformSummitData,
} from "~/lib/server/summit";
import type { SessionFooterVariant } from "~/lib/shared/types/summit";
import { z } from "zod";

// Get active summit with all data
export const getActiveSummitData = createServerFn({ method: "GET" })
  .middleware([dbMiddleware, authMiddleware])
  .handler(async ({ context }) => {
    const activeSummit = await getActiveSummit();
    if (!activeSummit) {
      throw new Error("No active summit found");
    }

    const summitData = await getSummitWithDays(activeSummit.id);
    if (!summitData) {
      throw new Error("Summit data not found");
    }

    const userRegistration = await getUserSummitRegistration(
      context.user.id,
      activeSummit.id,
    );

    // Get all session IDs
    const sessionIds = summitData.days.flatMap((day) =>
      day.sessions.map((session) => session.id),
    );

    const sessionRegistrations = await getUserSessionRegistrations(
      context.user.id,
      sessionIds,
    );

    // Transform to frontend format
    const days = transformSummitData(summitData.summit, summitData.days);

    // Create footer variants for each session
    const footerVariants = new Map<string, SessionFooterVariant>();

    summitData.days.forEach((day) => {
      day.sessions.forEach((session) => {
        if (session.recordingUrl) {
          footerVariants.set(session.id, {
            type: "viewing-recording",
            recordingUrl: session.recordingUrl,
            isVipOnly: session.isVipOnly,
          });
        } else {
          footerVariants.set(session.id, {
            type: "registration",
            isRegistered: sessionRegistrations.get(session.id) ?? false,
            registrationUrl: `/summit/sessions/${session.id}/register`,
          });
        }
      });
    });

    return {
      summit: summitData.summit,
      days,
      userRegistration,
      footerVariants: Object.fromEntries(footerVariants),
    };
  });

export const getActiveSummitDataOptions = queryOptions({
  queryKey: ["summit", "active"],
  queryFn: () => getActiveSummitData(),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

// Get session data for public session page
export const getSessionData = createServerFn({ method: "GET" })
  .middleware([dbMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }) => {
    const sessionData = await getSessionWithSummit(data.sessionId);
    if (!sessionData) {
      throw new Error("Session not found");
    }

    // Transform session to frontend format
    const session = {
      id: sessionData.session.id,
      title: sessionData.session.title,
      description: sessionData.session.description,
      fullDescription: sessionData.session.fullDescription ?? undefined,
      speaker: {
        id: sessionData.session.speaker.id,
        name: sessionData.session.speaker.name,
        title: sessionData.session.speaker.title,
        company: undefined,
        avatarUrl: sessionData.session.speaker.avatarUrl,
        bio: sessionData.session.speaker.bio ?? undefined,
        linkedInUrl: sessionData.session.speaker.linkedInUrl ?? undefined,
        twitterUrl: sessionData.session.speaker.twitterUrl ?? undefined,
        websiteUrl: sessionData.session.speaker.websiteUrl ?? undefined,
      },
      date: sessionData.session.date,
      startTime: sessionData.session.startTime,
      endTime: sessionData.session.endTime,
      timezone: "PST",
      topics: sessionData.session.topics,
      isVipOnly: sessionData.session.isVipOnly,
      recordingUrl: sessionData.session.recordingUrl ?? undefined,
      sessionFormat: sessionData.session.sessionFormat,
    };

    return {
      session,
      summit: sessionData.summit,
    };
  });

export const getSessionDataOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ["session", sessionId],
    queryFn: () => getSessionData({ data: { sessionId } }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

// Register for a session
export const registerForSession = createServerFn({ method: "POST" })
  .middleware([dbMiddleware, authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    await registerForSessionDb(context.user.id, data.sessionId);
    return { success: true };
  });

// Unregister from a session
export const unregisterFromSession = createServerFn({ method: "POST" })
  .middleware([dbMiddleware, authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    await unregisterFromSessionDb(context.user.id, data.sessionId);
    return { success: true };
  });
